import React, { useState, useMemo } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../../App";
import { downloadRowsAsXlsx } from "../../utils/excelDownload";

// ── Constants ──────────────────────────────────────────────────────────────
const COMPANY_LABELS = { all: "All Companies", ebek: "Ebek", ebekmed: "Ebek Med" };
const COMPANY_ACCENT = { ebek: "#E31E24", ebekmed: "#2AB5A5", all: "#7C3AED" };
const COMPANY_BG     = { ebek: "#FEE2E2", ebekmed: "#CCFBF1" };
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const CATEGORY_ICONS = {
  Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽",
  Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌",
};
const CATEGORY_COLORS = {
  Travel:       "#4f46e5",
  Hotel:        "#0891b2",
  Food:         "#059669",
  Fuel:         "#d97706",
  Courier:      "#7C3AED",
  Stationeries: "#E31E24",
  Prints:       "#ec4899",
  Miscellaneous:"#6b7280",
};
const CAT_COLOR_LIST = ["#4f46e5","#0891b2","#059669","#d97706","#7C3AED","#E31E24","#ec4899","#6b7280","#0d9488","#ea580c"];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

// ── Helpers ────────────────────────────────────────────────────────────────
function fmt(n) { return parseFloat(n||0).toLocaleString("en-IN", { minimumFractionDigits: 2 }); }
function fmtShort(n) {
  const v = parseFloat(n || 0);
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000)   return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${Math.round(v)}`;
}

function exportCSV(rows, from, to, company) {
  if (!rows.length) return;
  const companyLabel = COMPANY_LABELS[company] || "All Companies";
  const header = [
    "Company","Employee","Email","Department",
    "Trip","Purpose","Destination","Trip Start","Trip End","Trip Status",
    "Expense Date","Category","Description","Bill Type","Amount (₹)",
    "Advance Issued (₹)","Total Trip Expenses (₹)",
  ];
  const dataRows = rows.map(r => [
    COMPANY_LABELS[r.employee_company] || r.employee_company,
    r.employee_name, r.employee_email, r.department || "",
    r.trip_title, r.trip_purpose || "", r.destination || "",
    r.start_date ? new Date(r.start_date).toLocaleDateString("en-IN") : "",
    r.end_date   ? new Date(r.end_date).toLocaleDateString("en-IN")   : "",
    r.trip_status,
    r.expense_date ? new Date(r.expense_date).toLocaleDateString("en-IN") : "",
    r.category, r.description,
    r.bill_type === "cash_voucher" ? "Cash Voucher" : "Bill",
    parseFloat(r.amount||0).toFixed(2),
    parseFloat(r.advance_amount||0).toFixed(2),
    parseFloat(r.total_expenses||0).toFixed(2),
  ]);
  const totalSpent = rows.reduce((s, r) => s + parseFloat(r.amount||0), 0);
  downloadRowsAsXlsx({
    filename:`Ebek_Expense_Report_${companyLabel.replace(/\s+/g,"_")}_${from}_to_${to}.xlsx`,
    title:`EBEK EXPENSE REPORT — ${companyLabel}`,
    subtitleRows:[`Period: ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, `Generated: ${new Date().toLocaleString("en-IN")}`],
    headers:header, rows:dataRows,
    totalRow:["","","","","","","","","","","","","","GRAND TOTAL", totalSpent,""],
  });
}

function exportSummaryCSV(rows, from, to, company) {
  if (!rows.length) return;
  const companyLabel = COMPANY_LABELS[company] || "All Companies";
  const header = ["Company","Employee","Department","Trips","Expense Items","Total Spent (₹)","Total Advance (₹)","Balance (₹)"];
  const dataRows = rows.map(r => [
    COMPANY_LABELS[r.employee_company] || r.employee_company,
    r.employee_name, r.department || "",
    r.trip_count, r.expense_count,
    parseFloat(r.total_spent||0).toFixed(2),
    parseFloat(r.total_advance||0).toFixed(2),
    (parseFloat(r.total_advance||0) - parseFloat(r.total_spent||0)).toFixed(2),
  ]);
  downloadRowsAsXlsx({
    filename:`Ebek_Summary_${companyLabel.replace(/\s+/g,"_")}_${from}_to_${to}.xlsx`,
    title:`EBEK EXPENSE SUMMARY — ${companyLabel}`,
    subtitleRows:[`Period: ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, `Generated: ${new Date().toLocaleString("en-IN")}`],
    headers:header, rows:dataRows,
  });
}

function exportCompanyPaidCabReport(rows, from, to, company) {
  const cabRows = rows.filter(r => r.category === "Travel" && r.travel_subcategory === "Cab" && r.payment_source === "company");
  if (!cabRows.length) return false;
  const companyLabel = COMPANY_LABELS[company] || "All Companies";
  const headers = [
    "Company", "Employee", "Email", "Department", "Trip", "Purpose", "Destination",
    "Expense Date", "Cab Type", "Charge Type", "From", "To", "KMS", "Description",
    "Amount (₹)", "Recorded By",
  ];
  const dataRows = cabRows.map(r => [
    COMPANY_LABELS[r.employee_company] || r.employee_company,
    r.employee_name, r.employee_email, r.department || "",
    r.trip_title, r.trip_purpose || "", r.destination || "",
    r.expense_date ? new Date(r.expense_date).toLocaleDateString("en-IN") : "",
    r.cab_type === "personal_car" ? "Personal Car" : r.cab_type === "hired_cab" ? "Hired Cab" : (r.cab_type || ""),
    r.cab_charge_type === "full_day" ? "Full Day" : r.cab_charge_type === "kms_based" ? "KMS Based" : (r.cab_charge_type || ""),
    r.travel_from || "", r.travel_to || "", r.cab_kms_travelled || r.kms_travelled || "",
    r.description || "", parseFloat(r.amount || 0).toFixed(2), r.company_cost_recorded_by || r.employee_name,
  ]);
  const total = cabRows.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);
  downloadRowsAsXlsx({
    filename: `Ebek_Company_Paid_Cabs_${companyLabel.replace(/\s+/g, "_")}_${from}_to_${to}.xlsx`,
    title: `COMPANY-PAID CAB REPORT — ${companyLabel}`,
    subtitleRows: [
      `Period: ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`,
      "These costs are recorded for Finance and excluded from employee reimbursement and advance settlement.",
    ],
    headers, rows: dataRows,
    totalRow: ["", "", "", "", "", "", "", "", "", "", "", "", "", "GRAND TOTAL", total, ""],
  });
  return true;
}

// ── Specialised Export Helpers ────────────────────────────────────────────

function buildCsvBlob(titleLine, header, dataRows, totalRow) {
  return { titleLine, header, dataRows, totalRow };
}

function triggerDownload(report, filename) {
  downloadRowsAsXlsx({ filename, title:report.titleLine,
    subtitleRows:[`Generated: ${new Date().toLocaleString("en-IN")}`],
    headers:report.header, rows:report.dataRows, totalRow:report.totalRow });
}

// Employee-wise summary
function exportEmployeeWise(rows, from, to, company) {
  if (!rows.length) return;
  const companyLabel = COMPANY_LABELS[company] || "All Companies";
  const empMap = {};
  rows.forEach(r => {
    const key = r.employee_email;
    if (!empMap[key]) empMap[key] = { name: r.employee_name, email: r.employee_email, dept: r.department || "", company: COMPANY_LABELS[r.employee_company] || r.employee_company, trips: new Set(), items: 0, total: 0, advance: 0 };
    empMap[key].trips.add(r.trip_title);
    empMap[key].items += 1;
    empMap[key].total += parseFloat(r.amount||0);
    empMap[key].advance = Math.max(empMap[key].advance, parseFloat(r.advance_amount||0));
  });
  const header = ["Company","Employee","Email","Department","Trips","Expense Items","Total Spent (₹)","Total Advance (₹)","Balance (₹)"];
  const dataRows = Object.values(empMap).sort((a,b) => b.total - a.total).map(e => [
    e.company, e.name, e.email, e.dept, e.trips.size, e.items,
    e.total.toFixed(2), e.advance.toFixed(2), (e.advance - e.total).toFixed(2),
  ]);
  const grand = Object.values(empMap).reduce((s,e) => s + e.total, 0);
  const blob = buildCsvBlob(`EMPLOYEE-WISE EXPENSE REPORT — ${companyLabel} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["","","","","","GRAND TOTAL", grand.toFixed(2),"",""]);
  triggerDownload(blob, `Employee_Wise_${companyLabel.replace(/\s+/g,"_")}_${from}_to_${to}.csv`);
}

// Test-type exports — filter by trip_purpose keyword
function exportTestType(rows, from, to, company, testLabel, purposeFilter) {
  const filtered = rows.filter(r => {
    const p = (r.trip_purpose || "").toLowerCase();
    return purposeFilter(p);
  });
  if (!filtered.length) { alert(`No expenses found for ${testLabel} trips in this period.`); return; }
  const companyLabel = COMPANY_LABELS[company] || "All Companies";
  const header = ["Company","Employee","Department","Trip","Destination","Date","Category","Description","Bill Type","Amount (₹)"];
  const dataRows = filtered.map(r => [
    COMPANY_LABELS[r.employee_company] || r.employee_company,
    r.employee_name, r.department || "",
    r.trip_title, r.destination || "",
    r.expense_date ? new Date(r.expense_date).toLocaleDateString("en-IN") : "",
    r.category, r.description,
    r.bill_type === "cash_voucher" ? "Cash Voucher" : "Bill",
    parseFloat(r.amount||0).toFixed(2),
  ]);
  const grand = filtered.reduce((s,r) => s + parseFloat(r.amount||0), 0);
  const blob = buildCsvBlob(`${testLabel.toUpperCase()} EXPENSE REPORT — ${companyLabel} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["","","","","","","","","GRAND TOTAL", grand.toFixed(2)]);
  triggerDownload(blob, `${testLabel.replace(/\s+/g,"_")}_${companyLabel.replace(/\s+/g,"_")}_${from}_to_${to}.csv`);
}

// Marketing expenses-wise
function exportMarketingWise(rows, from, to, company) {
  const filtered = rows.filter(r => (r.department || "").toLowerCase().includes("marketing"));
  if (!filtered.length) { alert("No Marketing department expenses found in this period."); return; }
  const companyLabel = COMPANY_LABELS[company] || "All Companies";
  const header = ["Company","Employee","Trip","Date","Category","Description","Bill Type","Amount (₹)"];
  const dataRows = filtered.map(r => [
    COMPANY_LABELS[r.employee_company] || r.employee_company,
    r.employee_name, r.trip_title,
    r.expense_date ? new Date(r.expense_date).toLocaleDateString("en-IN") : "",
    r.category, r.description,
    r.bill_type === "cash_voucher" ? "Cash Voucher" : "Bill",
    parseFloat(r.amount||0).toFixed(2),
  ]);
  const grand = filtered.reduce((s,r) => s + parseFloat(r.amount||0), 0);
  const blob = buildCsvBlob(`MARKETING EXPENSE REPORT — ${companyLabel} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["","","","","","","GRAND TOTAL", grand.toFixed(2)]);
  triggerDownload(blob, `Marketing_Expenses_${companyLabel.replace(/\s+/g,"_")}_${from}_to_${to}.csv`);
}

// Trip-wise summary
function exportTripWise(rows, from, to, company) {
  if (!rows.length) return;
  const companyLabel = COMPANY_LABELS[company] || "All Companies";
  const tripMap = {};
  rows.forEach(r => {
    const key = r.trip_title + "|" + r.employee_name;
    if (!tripMap[key]) tripMap[key] = { emp: r.employee_name, company: COMPANY_LABELS[r.employee_company] || r.employee_company, dept: r.department || "", trip: r.trip_title, purpose: r.trip_purpose || "", destination: r.destination || "", start: r.start_date, end: r.end_date, status: r.trip_status, items: 0, total: 0, advance: parseFloat(r.advance_amount||0) };
    tripMap[key].items += 1;
    tripMap[key].total += parseFloat(r.amount||0);
  });
  const header = ["Company","Employee","Department","Trip","Purpose","Destination","Start Date","End Date","Status","Expense Items","Total Spent (₹)","Advance (₹)","Balance (₹)"];
  const dataRows = Object.values(tripMap).sort((a,b) => b.total - a.total).map(t => [
    t.company, t.emp, t.dept, t.trip, t.purpose, t.destination,
    t.start ? new Date(t.start).toLocaleDateString("en-IN") : "",
    t.end   ? new Date(t.end).toLocaleDateString("en-IN")   : "",
    t.status, t.items, t.total.toFixed(2), t.advance.toFixed(2), (t.advance - t.total).toFixed(2),
  ]);
  const grand = Object.values(tripMap).reduce((s,t) => s + t.total, 0);
  const blob = buildCsvBlob(`TRIP-WISE EXPENSE REPORT — ${companyLabel} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["","","","","","","","","","GRAND TOTAL", grand.toFixed(2),"",""]);
  triggerDownload(blob, `Trip_Wise_${companyLabel.replace(/\s+/g,"_")}_${from}_to_${to}.csv`);
}

// City-wise summary
function exportCityWise(rows, from, to, company) {
  if (!rows.length) return;
  const companyLabel = COMPANY_LABELS[company] || "All Companies";
  const cityMap = {};
  rows.forEach(r => {
    const key = (r.destination || "Unknown").trim();
    if (!cityMap[key]) cityMap[key] = { city: key, trips: new Set(), employees: new Set(), items: 0, total: 0 };
    cityMap[key].trips.add(r.trip_title);
    cityMap[key].employees.add(r.employee_email);
    cityMap[key].items += 1;
    cityMap[key].total += parseFloat(r.amount||0);
  });
  const header = ["City / Destination","Unique Trips","Unique Employees","Expense Items","Total Spent (₹)"];
  const dataRows = Object.values(cityMap).sort((a,b) => b.total - a.total).map(c => [
    c.city, c.trips.size, c.employees.size, c.items, c.total.toFixed(2),
  ]);
  const grand = Object.values(cityMap).reduce((s,c) => s + c.total, 0);
  const blob = buildCsvBlob(`CITY-WISE EXPENSE REPORT — ${companyLabel} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["","","","GRAND TOTAL", grand.toFixed(2)]);
  triggerDownload(blob, `City_Wise_${companyLabel.replace(/\s+/g,"_")}_${from}_to_${to}.csv`);
}

// ── SVG Math ───────────────────────────────────────────────────────────────
function polar(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, outerR, innerR, startDeg, endDeg) {
  const delta = Math.min(endDeg - startDeg, 359.9999);
  const end   = startDeg + delta;
  const large = delta > 180 ? 1 : 0;
  const o1 = polar(cx, cy, outerR, startDeg);
  const o2 = polar(cx, cy, outerR, end);
  const i1 = polar(cx, cy, innerR, end);
  const i2 = polar(cx, cy, innerR, startDeg);
  return `M${o1.x},${o1.y} A${outerR},${outerR},0,${large},1,${o2.x},${o2.y} L${i1.x},${i1.y} A${innerR},${innerR},0,${large},0,${i2.x},${i2.y} Z`;
}

// ── Donut Chart ────────────────────────────────────────────────────────────
function DonutChart({ segments, size = 180, thickness = 38 }) {
  const [hov, setHov] = useState(null);
  const total = segments.reduce((s, d) => s + d.value, 0);
  if (!total) return (
    <div style={{ height: size, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 13 }}>
      No data
    </div>
  );
  const cx = size / 2, cy = size / 2;
  const outerR = size / 2 - 6;
  const innerR = outerR - thickness;
  let cum = 0;
  const segs = segments.map((d, i) => {
    const start = cum * 360;
    cum += d.value / total;
    const end   = cum * 360;
    return { ...d, start, end, pct: (d.value / total * 100).toFixed(1) };
  });
  const hovSeg = hov != null ? segs[hov] : null;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: "visible", flexShrink: 0 }}>
      {segs.map((s, i) => (
        <path
          key={i}
          d={arcPath(cx, cy, hov === i ? outerR + 7 : outerR, innerR, s.start, s.end)}
          fill={s.color}
          opacity={hov != null && hov !== i ? 0.35 : 1}
          style={{ cursor: "pointer", transition: "all 0.18s ease" }}
          onMouseEnter={() => setHov(i)}
          onMouseLeave={() => setHov(null)}
        />
      ))}
      {/* Center label */}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize={11} fontWeight="800" fill="#1f2937">
        {hovSeg ? fmtShort(hovSeg.value) : fmtShort(total)}
      </text>
      <text x={cx} y={cy + 11} textAnchor="middle" fontSize={9} fill={hovSeg ? hovSeg.color : "#9ca3af"} fontWeight="700">
        {hovSeg ? `${hovSeg.pct}%` : "Total"}
      </text>
      {hovSeg && (
        <text x={cx} y={cy + 26} textAnchor="middle" fontSize={8} fill={hovSeg.color}>
          {hovSeg.label}
        </text>
      )}
    </svg>
  );
}

function DonutLegend({ segments, total, compact }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 5 : 8 }}>
      {segments.map((s, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
          <span style={{ fontSize: compact ? 11 : 12, color: "#374151", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {CATEGORY_ICONS[s.label] || "📌"} {s.label}
          </span>
          <span style={{ fontSize: compact ? 11 : 12, fontWeight: 700, color: "#1f2937", marginLeft: 4 }}>
            {fmtShort(s.value)}
          </span>
          <span style={{ fontSize: 10, color: "#9ca3af", width: 36, textAlign: "right", flexShrink: 0 }}>
            {(s.value / total * 100).toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Line Chart (Monthly Trend) ─────────────────────────────────────────────
function LineChart({ data, color = "#7C3AED" }) {
  const [hovIdx, setHovIdx] = useState(null);
  if (!data.length) return (
    <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 13 }}>
      Not enough data for a trend
    </div>
  );
  const W = 540, H = 160;
  const PAD = { t: 18, r: 16, b: 32, l: 52 };
  const iW  = W - PAD.l - PAD.r;
  const iH  = H - PAD.t - PAD.b;
  const maxV = Math.max(...data.map(d => d.total), 1);
  const pts  = data.map((d, i) => ({
    x: PAD.l + (data.length === 1 ? iW / 2 : (i / (data.length - 1)) * iW),
    y: PAD.t + iH - (d.total / maxV) * iH,
    ...d,
  }));
  const lineD = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaD = pts.length > 1
    ? `${lineD} L${pts[pts.length - 1].x},${PAD.t + iH} L${pts[0].x},${PAD.t + iH} Z`
    : "";
  const gridY = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    y: PAD.t + iH - f * iH,
    label: f === 0 ? "0" : fmtShort(maxV * f),
  }));
  const gradId = `lg-${color.replace("#","")}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", overflow: "visible" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {gridY.map((g, i) => (
        <g key={i}>
          <line x1={PAD.l} y1={g.y} x2={W - PAD.r} y2={g.y} stroke="#f3f4f6" strokeWidth={1} />
          <text x={PAD.l - 5} y={g.y + 3.5} textAnchor="end" fontSize={9} fill="#9ca3af">{g.label}</text>
        </g>
      ))}
      {/* Area */}
      {areaD && <path d={areaD} fill={`url(#${gradId})`} />}
      {/* Line */}
      <path d={lineD} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      {/* Points + x-labels */}
      {pts.map((p, i) => (
        <g key={i} onMouseEnter={() => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)} style={{ cursor: "default" }}>
          <circle cx={p.x} cy={p.y} r={hovIdx === i ? 6 : 4} fill={color} stroke="white" strokeWidth={2} style={{ transition: "r 0.15s" }} />
          {/* Tooltip */}
          {hovIdx === i && (
            <g>
              <rect x={p.x - 34} y={p.y - 30} width={68} height={20} rx={4} fill={color} />
              <text x={p.x} y={p.y - 17} textAnchor="middle" fontSize={9} fill="white" fontWeight="700">
                {fmtShort(p.total)}
              </text>
            </g>
          )}
          <text x={p.x} y={H - 5} textAnchor="middle" fontSize={9} fill="#9ca3af">{p.label}</text>
        </g>
      ))}
    </svg>
  );
}

// ── Analytics View ─────────────────────────────────────────────────────────
function AnalyticsView({ rows, summary, company, isSuperadmin }) {
  const [drillEmp, setDrillEmp] = useState(null);

  // ── Aggregations ──────────────────────────────────────────────────────────
  const catAgg = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const amt = parseFloat(r.amount || 0);
      map[r.category] = (map[r.category] || 0) + amt;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([cat, total], i) => ({
        label: cat,
        value: total,
        color: CATEGORY_COLORS[cat] || CAT_COLOR_LIST[i % CAT_COLOR_LIST.length],
        icon:  CATEGORY_ICONS[cat] || "📌",
      }));
  }, [rows]);

  const empAgg = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const amt = parseFloat(r.amount || 0);
      if (!map[r.employee_name]) {
        map[r.employee_name] = {
          name: r.employee_name,
          company: r.employee_company,
          dept: r.department,
          total: 0,
          categories: {},
          trips: {},
        };
      }
      const e = map[r.employee_name];
      e.total += amt;
      e.categories[r.category] = (e.categories[r.category] || 0) + amt;
      if (r.trip_id && !e.trips[r.trip_id]) {
        e.trips[r.trip_id] = {
          id:       r.trip_id,
          title:    r.trip_title,
          dest:     r.destination,
          advance:  parseFloat(r.advance_amount || 0),
          spent:    parseFloat(r.total_expenses  || 0),
          status:   r.trip_status,
        };
      }
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [rows]);

  const monthAgg = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if (!r.expense_date) return;
      const month = String(r.expense_date).substring(0, 7);
      map[month] = (map[month] || 0) + parseFloat(r.amount || 0);
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, total]) => ({
        label: new Date(month + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" }),
        total,
      }));
  }, [rows]);

  const compAgg = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const amt = parseFloat(r.amount || 0);
      const co  = r.employee_company;
      if (!map[co]) map[co] = { name: COMPANY_LABELS[co] || co, color: COMPANY_ACCENT[co] || "#6b7280", total: 0, categories: {} };
      map[co].total += amt;
      map[co].categories[r.category] = (map[co].categories[r.category] || 0) + amt;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [rows]);

  const tripEff = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if (!r.trip_id) return;
      if (!map[r.trip_id]) {
        map[r.trip_id] = {
          id:       r.trip_id,
          title:    r.trip_title,
          employee: r.employee_name,
          company:  r.employee_company,
          dest:     r.destination,
          advance:  parseFloat(r.advance_amount  || 0),
          spent:    parseFloat(r.total_expenses   || 0),
          status:   r.trip_status,
        };
      }
    });
    return Object.values(map).filter(t => t.advance > 0 || t.spent > 0);
  }, [rows]);

  // ── Derived KPIs ──────────────────────────────────────────────────────────
  const totalSpent   = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const totalAdv     = summary.reduce((s, r) => s + parseFloat(r.total_advance || 0), 0);
  const cashVouchers = rows.filter(r => r.bill_type === "cash_voucher");
  const cashAmt      = cashVouchers.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const cashPct      = totalSpent > 0 ? (cashAmt / totalSpent * 100).toFixed(1) : 0;
  const overspendTrips = tripEff.filter(t => t.spent > t.advance);
  const advUtil        = totalAdv > 0 ? Math.min(totalSpent / totalAdv * 100, 999).toFixed(1) : 0;
  const topCat         = catAgg[0];
  const topEmp         = empAgg[0];

  // ── Drilldown employee data ───────────────────────────────────────────────
  const drillData = drillEmp ? empAgg.find(e => e.name === drillEmp) : null;
  const drillCatSegs = drillData
    ? Object.entries(drillData.categories)
        .sort((a, b) => b[1] - a[1])
        .map(([cat, val], i) => ({
          label: cat,
          value: val,
          color: CATEGORY_COLORS[cat] || CAT_COLOR_LIST[i % CAT_COLOR_LIST.length],
        }))
    : [];

  const maxEmpSpend = empAgg[0]?.total || 1;

  // ── Auto-insights (top 3 smart callouts for CEO/CFO) ─────────────────────
  const insights = [];
  if (overspendTrips.length)
    insights.push({ icon: "⚠️", color: "#DC2626", bg: "#FEF2F2",
      text: `${overspendTrips.length} trip${overspendTrips.length > 1 ? "s" : ""} overspent their advance — total overrun of ${fmtShort(overspendTrips.reduce((s,t) => s + (t.spent - t.advance), 0))}` });
  if (parseFloat(cashPct) > 30)
    insights.push({ icon: "🧾", color: "#d97706", bg: "#FFFBEB",
      text: `${cashPct}% of spend is via cash vouchers — consider reviewing for compliance` });
  if (topEmp && totalSpent > 0)
    insights.push({ icon: "👤", color: "#7C3AED", bg: "#F5F3FF",
      text: `${topEmp.name.split(" ")[0]} accounts for ${(topEmp.total / totalSpent * 100).toFixed(0)}% of total spend (${fmtShort(topEmp.total)})` });
  if (topCat && totalSpent > 0 && (topCat.value / totalSpent) > 0.4)
    insights.push({ icon: "📊", color: "#0891b2", bg: "#F0F9FF",
      text: `${topCat.label} dominates at ${(topCat.value / totalSpent * 100).toFixed(0)}% of all expenses` });

  // ── Styles ────────────────────────────────────────────────────────────────
  const card = (extra = {}) => ({
    background: "var(--bg-card)",
    borderRadius: 14,
    border: "1px solid var(--border)",
    padding: "20px 22px",
    ...extra,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── Insights Strip ──────────────────────────────────────────── */}
      {insights.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {insights.map((ins, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderRadius: 10, background: ins.bg, border: `1px solid ${ins.color}22` }}>
              <span style={{ fontSize: 18 }}>{ins.icon}</span>
              <span style={{ fontSize: 13, color: ins.color, fontWeight: 600 }}>{ins.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── KPI Cards ───────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
        {[
          { label: "Total Spend",      value: fmtShort(totalSpent),  color: "#E31E24", bg: "#FEE2E2", icon: "💸" },
          { label: "Advance Issued",   value: fmtShort(totalAdv),    color: "#d97706", bg: "#FEF3C7", icon: "💰" },
          { label: "Utilization",      value: `${advUtil}%`,
            color: parseFloat(advUtil) > 100 ? "#DC2626" : parseFloat(advUtil) > 85 ? "#d97706" : "#059669",
            bg:    parseFloat(advUtil) > 100 ? "#FEE2E2" : parseFloat(advUtil) > 85 ? "#FEF3C7" : "#D1FAE5",
            icon: parseFloat(advUtil) > 100 ? "🔴" : parseFloat(advUtil) > 85 ? "🟡" : "🟢" },
          { label: "Cash Voucher %",   value: `${cashPct}%`,
            color: parseFloat(cashPct) > 50 ? "#DC2626" : parseFloat(cashPct) > 30 ? "#d97706" : "#059669",
            bg:    parseFloat(cashPct) > 50 ? "#FEE2E2" : parseFloat(cashPct) > 30 ? "#FEF3C7" : "#D1FAE5",
            icon: "🧾" },
          { label: "Overspend Trips",  value: overspendTrips.length, color: overspendTrips.length ? "#DC2626" : "#059669", bg: overspendTrips.length ? "#FEE2E2" : "#D1FAE5", icon: overspendTrips.length ? "⚠️" : "✅" },
          { label: "Employees",        value: empAgg.length,          color: "#7C3AED", bg: "#EDE9FE", icon: "👥" },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: s.color, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.75 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.icon} {s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Category Donut + Top Spenders ───────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* Category Breakdown */}
        <div style={card()}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", marginBottom: 16 }}>
            📊 Spend by Category
          </div>
          {catAgg.length === 0 ? (
            <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, padding: "20px 0" }}>No data</div>
          ) : (
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <DonutChart segments={catAgg} size={160} thickness={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <DonutLegend segments={catAgg} total={totalSpent} compact />
              </div>
            </div>
          )}
        </div>

        {/* Top Spenders */}
        <div style={card()}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>
              🏆 Top Spenders
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>Click to drilldown</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {empAgg.slice(0, 8).map((emp, i) => {
              const pct    = (emp.total / maxEmpSpend) * 100;
              const coClr  = COMPANY_ACCENT[emp.company] || "#6b7280";
              const sel    = drillEmp === emp.name;
              return (
                <div
                  key={i}
                  onClick={() => setDrillEmp(sel ? null : emp.name)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                    padding: "5px 8px", borderRadius: 8,
                    background: sel ? `${coClr}15` : "transparent",
                    border: `1.5px solid ${sel ? coClr + "50" : "transparent"}`,
                    transition: "all 0.18s",
                  }}
                >
                  <span style={{ width: 18, height: 18, borderRadius: "50%", background: `${coClr}20`, border: `2px solid ${coClr}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: coClr, flexShrink: 0 }}>
                    {i + 1}
                  </span>
                  <span style={{ width: 90, fontSize: 12, fontWeight: 600, color: "var(--text-primary)", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {emp.name.split(" ").slice(0, 2).join(" ")}
                  </span>
                  <div style={{ flex: 1, height: 20, background: "#f3f4f6", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg,${coClr}99,${coClr})`, borderRadius: 4, minWidth: pct > 0 ? 4 : 0, transition: "width 0.5s ease" }} />
                  </div>
                  <span style={{ width: 58, fontSize: 11, fontWeight: 800, color: coClr, textAlign: "right", flexShrink: 0 }}>
                    {fmtShort(emp.total)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Monthly Trend ────────────────────────────────────────────── */}
      <div style={card()}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", marginBottom: 16 }}>
          📈 Monthly Spend Trend
        </div>
        <LineChart data={monthAgg} color="#7C3AED" />
      </div>

      {/* ── Company Split (superadmin, multiple companies) ───────────── */}
      {isSuperadmin && compAgg.length > 1 && (
        <div style={card()}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", marginBottom: 20 }}>
            🏢 Company Comparison
          </div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${compAgg.length}, 1fr)`, gap: 16 }}>
            {compAgg.map((co, i) => {
              const coSegs = Object.entries(co.categories)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, val], j) => ({
                  label: cat, value: val,
                  color: CATEGORY_COLORS[cat] || CAT_COLOR_LIST[j % CAT_COLOR_LIST.length],
                }));
              const coTotal = totalSpent > 0 ? (co.total / totalSpent * 100).toFixed(1) : 0;
              return (
                <div key={i} style={{ textAlign: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", marginBottom: 12 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: co.color }} />
                    <span style={{ fontSize: 13, fontWeight: 800, color: co.color }}>{co.name}</span>
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>({coTotal}%)</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                    <DonutChart segments={coSegs} size={150} thickness={32} />
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: co.color, marginBottom: 8 }}>{fmtShort(co.total)}</div>
                  <DonutLegend segments={coSegs} total={co.total} compact />
                </div>
              );
            })}
          </div>
          {/* Company bar comparison */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
            {compAgg.map((co, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ width: 80, fontSize: 12, fontWeight: 600, color: co.color }}>{co.name}</span>
                <div style={{ flex: 1, height: 24, background: "#f3f4f6", borderRadius: 6, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(co.total / (compAgg[0]?.total || 1)) * 100}%`, background: `linear-gradient(90deg, ${co.color}99, ${co.color})`, borderRadius: 6, display: "flex", alignItems: "center", paddingLeft: 8, transition: "width 0.6s ease" }}>
                    <span style={{ fontSize: 11, color: "white", fontWeight: 700, whiteSpace: "nowrap" }}>{fmtShort(co.total)}</span>
                  </div>
                </div>
                <span style={{ width: 48, fontSize: 11, color: "#9ca3af" }}>{((co.total / totalSpent) * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Bill Type Split ──────────────────────────────────────────── */}
      <div style={card()}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", marginBottom: 16 }}>
          🧾 Bill vs Cash Voucher Split
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {/* Visual bar */}
          <div>
            <div style={{ height: 32, borderRadius: 8, overflow: "hidden", display: "flex" }}>
              <div style={{ width: `${totalSpent > 0 ? ((totalSpent - cashAmt) / totalSpent * 100) : 50}%`, background: "linear-gradient(90deg,#059669aa,#059669)", display: "flex", alignItems: "center", justifyContent: "center", transition: "width 0.6s" }}>
                {(totalSpent - cashAmt) / totalSpent > 0.1 && (
                  <span style={{ fontSize: 10, color: "white", fontWeight: 700 }}>{((totalSpent - cashAmt) / totalSpent * 100).toFixed(0)}%</span>
                )}
              </div>
              <div style={{ flex: 1, background: "linear-gradient(90deg,#d97706aa,#d97706)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {cashAmt / totalSpent > 0.1 && (
                  <span style={{ fontSize: 10, color: "white", fontWeight: 700 }}>{cashPct}%</span>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: "#059669" }} />
                <span style={{ fontSize: 12, color: "#374151" }}>Proper Bills</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: "#d97706" }} />
                <span style={{ fontSize: 12, color: "#374151" }}>Cash Vouchers</span>
              </div>
            </div>
          </div>
          {/* Amounts */}
          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1, background: "#D1FAE5", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#059669", textTransform: "uppercase" }}>Bills</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#059669", marginTop: 4 }}>{fmtShort(totalSpent - cashAmt)}</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{rows.filter(r => r.bill_type !== "cash_voucher").length} items</div>
            </div>
            <div style={{ flex: 1, background: "#FEF3C7", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#d97706", textTransform: "uppercase" }}>Cash</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#d97706", marginTop: 4 }}>{fmtShort(cashAmt)}</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{cashVouchers.length} items</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Trip Efficiency (Advance vs Spent) ──────────────────────── */}
      {tripEff.length > 0 && (
        <div style={card()}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", marginBottom: 4 }}>
            ✈️ Advance vs Actual — Trip Efficiency
          </div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
            🟢 Under advance &nbsp;·&nbsp; 🔴 Overspent &nbsp;·&nbsp; Bars show spend as % of advance
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tripEff.slice(0, 12).map((t, i) => {
              const overspent = t.spent > t.advance;
              const pct       = t.advance > 0 ? Math.min((t.spent / t.advance) * 100, 150) : 100;
              const clr       = overspent ? "#DC2626" : "#059669";
              const balance   = t.advance - t.spent;
              return (
                <div key={i} style={{ padding: "10px 14px", borderRadius: 10, background: overspent ? "#FEF2F2" : "#F0FDF4", border: `1px solid ${clr}20` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 14 }}>{overspent ? "🔴" : "🟢"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.title}
                      </div>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>
                        {t.employee}{t.dest ? ` · 📍 ${t.dest}` : ""}
                        {isSuperadmin && <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: COMPANY_BG[t.company] || "#f3f4f6", color: COMPANY_ACCENT[t.company] || "#6b7280" }}>{COMPANY_LABELS[t.company] || t.company}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: clr }}>
                        {overspent ? `+${fmtShort(Math.abs(balance))} over` : `${fmtShort(balance)} left`}
                      </div>
                    </div>
                  </div>
                  {/* Dual-layer bar */}
                  <div style={{ position: "relative", height: 10, borderRadius: 5, background: "#e5e7eb", overflow: "visible" }}>
                    {/* Advance bar (background) */}
                    <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: "100%", borderRadius: 5, background: "#d1d5db" }} />
                    {/* Spent bar */}
                    <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: `${Math.min(pct, 100)}%`, borderRadius: 5, background: `linear-gradient(90deg, ${clr}99, ${clr})`, transition: "width 0.6s ease" }} />
                    {/* Overspend indicator */}
                    {overspent && (
                      <div style={{ position: "absolute", top: -2, right: 0, width: `${Math.min(pct - 100, 50)}%`, height: 14, background: "#DC262650", borderRadius: 4, border: "1px solid #DC2626" }} />
                    )}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: 10, color: "#6b7280" }}>
                    <span>Advance: {fmtShort(t.advance)}</span>
                    <span>Spent: <strong style={{ color: clr }}>{fmtShort(t.spent)}</strong></span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Employee Drilldown ───────────────────────────────────────── */}
      {drillData && (
        <div style={{ ...card(), border: `2px solid ${COMPANY_ACCENT[drillData.company] || "#7C3AED"}40`, background: `${COMPANY_ACCENT[drillData.company] || "#7C3AED"}06` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: `linear-gradient(135deg, ${COMPANY_ACCENT[drillData.company] || "#7C3AED"}, ${COMPANY_ACCENT[drillData.company] || "#7C3AED"}88)`, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 800, fontSize: 18 }}>
              {drillData.name[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "var(--text-primary)" }}>{drillData.name}</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {drillData.dept && `${drillData.dept} · `}
                <span style={{ color: COMPANY_ACCENT[drillData.company] || "#7C3AED", fontWeight: 700 }}>{COMPANY_LABELS[drillData.company] || drillData.company}</span>
                {" · "}{fmtShort(drillData.total)} total spend
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#7C3AED" }}>
              {totalSpent > 0 ? `${(drillData.total / totalSpent * 100).toFixed(1)}% of company spend` : ""}
            </div>
            <button onClick={() => setDrillEmp(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}>✕</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* Category breakdown for this employee */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", marginBottom: 12 }}>Category Breakdown</div>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <DonutChart segments={drillCatSegs} size={140} thickness={30} />
                <div style={{ flex: 1 }}>
                  <DonutLegend segments={drillCatSegs} total={drillData.total} compact />
                </div>
              </div>
            </div>
            {/* Trips for this employee */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", marginBottom: 12 }}>Trips in Period</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Object.values(drillData.trips).map((tr, i) => {
                  const bal = tr.advance - tr.spent;
                  const over = tr.spent > tr.advance;
                  return (
                    <div key={i} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--bg-surface)", border: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>{tr.title}</div>
                          {tr.dest && <div style={{ fontSize: 11, color: "#9ca3af" }}>📍 {tr.dest}</div>}
                        </div>
                        <span className={`badge badge-${tr.status}`} style={{ fontSize: 10 }}>{tr.status}</span>
                      </div>
                      <div style={{ display: "flex", gap: 12, marginTop: 6, fontSize: 11 }}>
                        <span style={{ color: "#d97706" }}>💰 {fmtShort(tr.advance)}</span>
                        <span style={{ color: "#E31E24" }}>💸 {fmtShort(tr.spent)}</span>
                        <span style={{ color: over ? "#DC2626" : "#059669", fontWeight: 700 }}>
                          {over ? `⚠️ +${fmtShort(Math.abs(bal))}` : `✅ ${fmtShort(bal)} left`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Reports Page ──────────────────────────────────────────────────────
export default function Reports() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin" || user?.role === "executive";

  const [mode,     setMode]     = useState("month");
  const [year,     setYear]     = useState(String(currentYear));
  const [month,    setMonth]    = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [fromDate, setFromDate] = useState("");
  const [toDate,   setToDate]   = useState("");
  const [company,  setCompany]  = useState(isSuperadmin ? "all" : (user?.company || "ebek"));
  const [loading,  setLoading]  = useState(false);
  const [data,     setData]     = useState(null);
  const [view,     setView]     = useState("analytics");

  const getDateRange = () => {
    if (mode === "custom") return { from: fromDate, to: toDate };
    if (mode === "year")   return { from: `${year}-01-01`, to: `${year}-12-31` };
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
    return { from: `${year}-${month}-01`, to: `${year}-${month}-${lastDay}` };
  };

  const handleGenerate = async () => {
    const { from, to } = getDateRange();
    if (!from || !to)  { toast.error("Please set a valid date range"); return; }
    if (from > to)     { toast.error("From date must be before To date"); return; }
    setLoading(true);
    setData(null);
    try {
      const params = `from=${from}&to=${to}&company=${company}`;
      const [rowsRes, summaryRes] = await Promise.all([
        API.get(`/reports/expenses?${params}`),
        API.get(`/reports/summary?${params}`),
      ]);
      setData({ rows: rowsRes.data, summary: summaryRes.data, from, to });
      setView("analytics");
      if (!rowsRes.data.length) toast("No expenses found for this period", { icon: "ℹ️" });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to generate report");
    } finally { setLoading(false); }
  };

  const totalSpent   = data ? data.rows.reduce((s, r) => s + parseFloat(r.amount||0), 0) : 0;
  const totalAdvance = data ? data.summary.reduce((s, r) => s + parseFloat(r.total_advance||0), 0) : 0;
  const companyPaidCabRows = data ? data.rows.filter(r => r.category === "Travel" && r.travel_subcategory === "Cab" && r.payment_source === "company") : [];
  const companyPaidCabTotal = companyPaidCabRows.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);
  const balance      = totalAdvance - totalSpent;

  const inputStyle = {
    padding: "9px 14px", borderRadius: 8, border: "1.5px solid var(--border)",
    fontSize: 14, background: "var(--bg-input)", color: "var(--text-primary)",
    outline: "none", fontFamily: "inherit", cursor: "pointer",
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 className="page-title">📊 Expense Reports & Analytics</h1>
        <p className="page-subtitle">CEO/CFO-grade insights — category breakdowns, spend trends, and employee drilldowns</p>
      </div>

      {/* Filter Card */}
      <div className="card" style={{ marginBottom: 24 }}>
        {/* Mode Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: 16 }}>
          {[
            { key: "month",  label: "📅 By Month"     },
            { key: "year",   label: "📆 By Year"       },
            { key: "custom", label: "🗓️ Custom Range"  },
          ].map(m => (
            <button key={m.key} onClick={() => setMode(m.key)}
              style={{ padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13,
                background: mode === m.key ? "#7C3AED" : "var(--bg-surface-2)",
                color:      mode === m.key ? "white"    : "var(--text-muted)",
                transition: "all 0.2s" }}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          {/* Company */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Company</div>
            <select value={company} onChange={e => setCompany(e.target.value)} style={inputStyle}>
              {isSuperadmin && <option value="all">🏢 All Companies</option>}
              <option value="ebek">Ebek</option>
              <option value="ebekmed">Ebek Med</option>
            </select>
          </div>
          {/* Year */}
          {(mode === "month" || mode === "year") && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Year</div>
              <select value={year} onChange={e => setYear(e.target.value)} style={inputStyle}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
          {/* Month */}
          {mode === "month" && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>Month</div>
              <select value={month} onChange={e => setMonth(e.target.value)} style={inputStyle}>
                {MONTHS.map((m, i) => (
                  <option key={i} value={String(i + 1).padStart(2, "0")}>{m}</option>
                ))}
              </select>
            </div>
          )}
          {/* Custom Range */}
          {mode === "custom" && (
            <>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>From Date</div>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase" }}>To Date</div>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={inputStyle} />
              </div>
            </>
          )}
          <button
            onClick={handleGenerate} disabled={loading}
            style={{ padding: "10px 28px", borderRadius: 8, border: "none", cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 700, fontSize: 14, marginTop: 22,
              background: loading ? "var(--border)" : "#7C3AED",
              color:      loading ? "var(--text-muted)" : "white",
              transition: "all 0.2s" }}>
            {loading ? "⏳ Loading..." : "🔍 Generate Report"}
          </button>
        </div>
        {/* Period preview */}
        {!loading && (() => {
          const { from, to } = getDateRange();
          if (!from) return null;
          return (
            <div style={{ marginTop: 14, fontSize: 13, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 8 }}>
              <span>📅</span>
              <span>Period: <strong style={{ color: "var(--text-primary)" }}>{new Date(from).toLocaleDateString("en-IN")} — {new Date(to).toLocaleDateString("en-IN")}</strong></span>
              <span>·</span>
              <span>Company: <strong style={{ color: "var(--text-primary)" }}>{COMPANY_LABELS[company] || company}</strong></span>
            </div>
          );
        })()}
      </div>

      {/* Results */}
      {data && (
        <>
          {/* View Toggle + Download */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 4, background: "var(--bg-surface-2)", borderRadius: 8, padding: 4 }}>
              {[
                { key: "analytics", label: "📈 Analytics" },
                { key: "detail",    label: "📋 Detailed"  },
                { key: "summary",   label: "👥 By Employee" },
              ].map(v => (
                <button key={v.key} onClick={() => setView(v.key)}
                  style={{ padding: "7px 16px", borderRadius: 6, border: "none", cursor: "pointer",
                    fontWeight: 700, fontSize: 13,
                    background: view === v.key ? "var(--bg-card)" : "transparent",
                    color:      view === v.key ? "#7C3AED" : "var(--text-muted)",
                    boxShadow:  view === v.key ? "var(--shadow-sm)" : "none",
                    transition: "all 0.2s" }}>
                  {v.label}
                </button>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button onClick={() => exportCSV(data.rows, data.from, data.to, company)}
                disabled={!data.rows.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, background: "#059669", color: "white", display: "flex", alignItems: "center", gap: 5 }}>
                📥 Detailed
              </button>
              <button onClick={() => exportSummaryCSV(data.summary, data.from, data.to, company)}
                disabled={!data.summary.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "1.5px solid var(--border)", cursor: "pointer", fontWeight: 700, fontSize: 12, background: "var(--bg-card)", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 5 }}>
                📊 Summary
              </button>
              <button onClick={() => {
                if (!exportCompanyPaidCabReport(data.rows, data.from, data.to, company)) {
                  toast("No company-paid cab entries were found for this period.");
                }
              }}
                disabled={!companyPaidCabRows.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "none", cursor: companyPaidCabRows.length ? "pointer" : "not-allowed", fontWeight: 700, fontSize: 12, background: companyPaidCabRows.length ? "#0f766e" : "var(--border)", color: "white", display: "flex", alignItems: "center", gap: 5 }}>
                🏢 Company-paid Cabs
              </button>
              <button onClick={() => exportEmployeeWise(data.rows, data.from, data.to, company)}
                disabled={!data.rows.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, background: "#4f46e5", color: "white", display: "flex", alignItems: "center", gap: 5 }}>
                👤 Emp-wise
              </button>
              <button onClick={() => exportTestType(data.rows, data.from, data.to, company, "OET Test", p => p.includes("oet"))}
                disabled={!data.rows.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, background: "#0891b2", color: "white", display: "flex", alignItems: "center", gap: 5 }}>
                📝 OET Test
              </button>
              <button onClick={() => exportTestType(data.rows, data.from, data.to, company, "Cambridge Test", p => p.includes("cambridge"))}
                disabled={!data.rows.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, background: "#7C3AED", color: "white", display: "flex", alignItems: "center", gap: 5 }}>
                🎓 Cambridge
              </button>
              <button onClick={() => exportTestType(data.rows, data.from, data.to, company, "Other Tests", p => !p.includes("oet") && !p.includes("cambridge") && (p.includes("test") || p.includes("exam") || p.includes("ielts") || p.includes("toefl") || p.includes("pte")))}
                disabled={!data.rows.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, background: "#D97706", color: "white", display: "flex", alignItems: "center", gap: 5 }}>
                📋 Other Tests
              </button>
              <button onClick={() => exportMarketingWise(data.rows, data.from, data.to, company)}
                disabled={!data.rows.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, background: "#E31E24", color: "white", display: "flex", alignItems: "center", gap: 5 }}>
                📣 Marketing
              </button>
              <button onClick={() => exportTripWise(data.rows, data.from, data.to, company)}
                disabled={!data.rows.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, background: "#065F46", color: "white", display: "flex", alignItems: "center", gap: 5 }}>
                ✈️ Trip-wise
              </button>
              <button onClick={() => exportCityWise(data.rows, data.from, data.to, company)}
                disabled={!data.rows.length}
                style={{ padding: "9px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, background: "#92400e", color: "white", display: "flex", alignItems: "center", gap: 5 }}>
                🏙️ City-wise
              </button>
            </div>
          </div>

          {companyPaidCabRows.length > 0 && (
            <div style={{ marginBottom:20, padding:"12px 16px", borderRadius:10, border:"1px solid #99f6e4", background:"#f0fdfa", color:"#115e59", fontSize:13 }}>
              <strong>🏢 Company-paid cab travel:</strong> {companyPaidCabRows.length} entr{companyPaidCabRows.length === 1 ? "y" : "ies"} totalling <strong>₹{fmt(companyPaidCabTotal)}</strong>. These costs are in the Finance report and excluded from employee claims and advance settlement.
            </div>
          )}

          {/* ── Analytics View ─────────────────────────────────────────── */}
          {view === "analytics" && (
            <AnalyticsView
              rows={data.rows}
              summary={data.summary}
              company={company}
              isSuperadmin={isSuperadmin}
            />
          )}

          {/* ── Detailed Table ─────────────────────────────────────────── */}
          {view === "detail" && (
            <div className="card table-responsive" style={{ padding: 0 }}>
              {data.rows.length === 0 ? (
                <div className="empty-state"><div style={{ fontSize: 48 }}>📭</div><h3>No expenses in this period</h3></div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Company</th><th>Employee</th><th>Dept</th>
                      <th>Trip</th><th>Destination</th><th>Date</th>
                      <th>Category</th><th>Description</th><th>Bill Type</th>
                      <th style={{ textAlign: "right" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={i}>
                        <td>
                          <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: COMPANY_BG[r.employee_company] || "var(--bg-surface)", color: COMPANY_ACCENT[r.employee_company] || "var(--text-muted)" }}>
                            {COMPANY_LABELS[r.employee_company] || r.employee_company}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>{r.employee_name}</td>
                        <td style={{ color: "var(--text-muted)", fontSize: 13 }}>{r.department || "—"}</td>
                        <td style={{ fontSize: 13, maxWidth: 160 }}>
                          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{r.trip_title}</div>
                          <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{r.trip_purpose}</div>
                        </td>
                        <td style={{ color: "var(--text-muted)", fontSize: 13 }}>{r.destination || "—"}</td>
                        <td style={{ fontSize: 13, whiteSpace: "nowrap", color: "var(--text-secondary)" }}>
                          {new Date(r.expense_date).toLocaleDateString("en-IN")}
                        </td>
                        <td>
                          <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13 }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: CATEGORY_COLORS[r.category] || "#6b7280", flexShrink: 0 }} />
                            {r.category}
                          </span>
                        </td>
                        <td style={{ color: "var(--text-muted)", fontSize: 13, maxWidth: 180 }}>{r.description}</td>
                        <td>
                          <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, background: r.bill_type === "cash_voucher" ? "rgba(217,119,6,0.12)" : "rgba(5,150,105,0.12)", color: r.bill_type === "cash_voucher" ? "#d97706" : "#059669" }}>
                            {r.bill_type === "cash_voucher" ? "Cash" : "Bill"}
                          </span>
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 700, color: "#E31E24", whiteSpace: "nowrap" }}>₹{fmt(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={9} style={{ textAlign: "right", fontWeight: 700, padding: "14px 16px", color: "var(--text-secondary)", background: "var(--bg-surface-2)" }}>Grand Total</td>
                      <td style={{ textAlign: "right", fontWeight: 800, fontSize: 16, color: "#E31E24", padding: "14px 16px", background: "var(--bg-surface-2)", whiteSpace: "nowrap" }}>₹{fmt(totalSpent)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}

          {/* ── Summary Table ───────────────────────────────────────────── */}
          {view === "summary" && (
            <div className="card table-responsive" style={{ padding: 0 }}>
              {data.summary.length === 0 ? (
                <div className="empty-state"><div style={{ fontSize: 48 }}>📭</div><h3>No data in this period</h3></div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Company</th><th>Employee</th><th>Dept</th>
                      <th style={{ textAlign: "center" }}>Trips</th>
                      <th style={{ textAlign: "center" }}>Items</th>
                      <th style={{ textAlign: "right" }}>Total Spent</th>
                      <th style={{ textAlign: "right" }}>Advance</th>
                      <th style={{ textAlign: "right" }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.summary.map((r, i) => {
                      const bal = parseFloat(r.total_advance||0) - parseFloat(r.total_spent||0);
                      return (
                        <tr key={i}>
                          <td>
                            <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: COMPANY_BG[r.employee_company] || "var(--bg-surface)", color: COMPANY_ACCENT[r.employee_company] || "var(--text-muted)" }}>
                              {COMPANY_LABELS[r.employee_company] || r.employee_company}
                            </span>
                          </td>
                          <td style={{ fontWeight: 700, color: "var(--text-primary)" }}>{r.employee_name}</td>
                          <td style={{ color: "var(--text-muted)", fontSize: 13 }}>{r.department || "—"}</td>
                          <td style={{ textAlign: "center", fontWeight: 600 }}>{r.trip_count}</td>
                          <td style={{ textAlign: "center", color: "var(--text-muted)" }}>{r.expense_count}</td>
                          <td style={{ textAlign: "right", fontWeight: 700, color: "#E31E24" }}>₹{fmt(r.total_spent)}</td>
                          <td style={{ textAlign: "right", fontWeight: 600, color: "#d97706" }}>₹{fmt(r.total_advance)}</td>
                          <td style={{ textAlign: "right", fontWeight: 700, color: bal >= 0 ? "#059669" : "#DC2626" }}>
                            {bal >= 0 ? "↩️" : "➕"} ₹{fmt(Math.abs(bal))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} style={{ textAlign: "right", fontWeight: 700, padding: "14px 16px", color: "var(--text-secondary)", background: "var(--bg-surface-2)" }}>Total</td>
                      <td style={{ textAlign: "right", fontWeight: 800, color: "#E31E24",  padding: "14px 16px", background: "var(--bg-surface-2)" }}>₹{fmt(totalSpent)}</td>
                      <td style={{ textAlign: "right", fontWeight: 800, color: "#d97706",  padding: "14px 16px", background: "var(--bg-surface-2)" }}>₹{fmt(totalAdvance)}</td>
                      <td style={{ textAlign: "right", fontWeight: 800, color: balance >= 0 ? "#059669" : "#DC2626", padding: "14px 16px", background: "var(--bg-surface-2)" }}>₹{fmt(Math.abs(balance))}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
